<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';
import { cn } from '@/lib/utils';

const props = defineProps({
  src: {
    type: String,
    required: true,
  },
  class: {
    type: String,
  },
  imageClass: {
    type: String,
  },
  alt: {
    type: String,
    default: '',
  },
  width: {
    type: [String, Number],
    default: '100%',
  },
  height: {
    type: [String, Number],
    default: 'auto',
  },
  loading: {
    type: String,
    default: 'lazy',
  },
});

const emit = defineEmits(['error']);

const imgRef = ref(null);
const isVisible = ref(false);
const isLoaded = ref(false);

let stopObserver = null;

onMounted(() => {
  stopObserver = useIntersectionObserver(
    imgRef,
    ([{ isIntersecting }]) => {
      if (isIntersecting) {
        isVisible.value = true;
        if (stopObserver) stopObserver.stop();
      }
    },
    {
      rootMargin: '0px 0px 200px 0px',
    },
  );
});

onUnmounted(() => {
  if (stopObserver) stopObserver.stop();
});

const handleLoad = () => {
  isLoaded.value = true;
};

const onError = (e) => {
  emit('error', e);
};
</script>

<template>
  <div ref="imgRef" :class="cn('image-lazy', props.class)">
    <img
      v-if="isVisible"
      decoding="async"
      :src="src"
      :alt="alt"
      :width="width"
      :height="height"
      :loading="loading"
      @load="handleLoad"
      @error="onError"
      :class="cn('w-full h-full', props.imageClass, isLoaded ? 'loaded' : '')"
    />
  </div>
</template>

<style scoped>
.image-lazy {
  position: relative;
  width: v-bind('typeof width === "string" ? width : `${width}px`');
  height: v-bind('typeof height === "string" ? height : `${height}px`');
}

img {
  opacity: 0;
  transition: opacity 0.3s ease;
}

img.loaded {
  opacity: 1;
}
</style>
